import { useState, useCallback, memo, useEffect } from 'react';
import type { UnifiedReaderLayoutProps, SidebarTab } from './types';
import { useTranslation } from '../../i18n/useTranslation';
import ReaderHeader from './ReaderHeader';
import ReaderSidebar from './ReaderSidebar';
import ReaderBottomToolbar from './ReaderBottomToolbar';
import SettingsPanel from './panels/SettingsPanel';
import FlashcardStudyMode from './FlashcardStudyMode';
import QuickActionMenu from './QuickActionMenu';
import {
  MobileBottomNav,
  BottomSheet
} from '../layout/index';
import type { NavItem } from '../layout/index';
import { FloatingVerticalDock } from './FloatingVerticalDock';
import { FloatingPanel, MobileFloatingPanel } from './FloatingPanel';
import {
  Search,
  BookOpen,
  Bookmark,
  StickyNote,
  Download,
  Settings,
  Printer,
  Info,
  Layers,
  MoreVertical
} from 'lucide-react';
import styles from './UnifiedReaderLayout.module.css';

/**
 * UnifiedReaderLayout - Modern Mobile-First Reader Interface
 *
 * Master container for all reader formats (PDF, EPUB, Custom HTML) with orange branding.
 *
 * **Mobile (<768px):**
 * - FloatingMobileDock at bottom for quick access
 * - MobileFloatingPanel for content (slides up from bottom)
 *
 * **Desktop (>=768px):**
 * - FloatingVerticalDock (right side) - macOS-style magnifying icons
 * - FloatingPanel for content (slides in from right with backdrop)
 *
 * Features:
 * - Orange gradient branding (CSS variable system)
 * - WCAG 2.2 AA compliant
 * - Touch-friendly (44px minimum targets)
 * - Framer Motion animations
 * - Proximity-based icon magnification (desktop)
 */
function UnifiedReaderLayout({
  bookId,
  format,
  metadata,
  navigation,
  navigationCallbacks,
  tableOfContents,
  bookmarks,
  notes,
  actions,
  onAddPageNote,
  children,
  floatingActions,
  theme = 'light',
  zoomControls,
  activeSecureContentChunkId = null,
}: UnifiedReaderLayoutProps) {
  const { t } = useTranslation();
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [studyDeckId, setStudyDeckId] = useState<string | null>(null);
  const [studyDeckName, setStudyDeckName] = useState<string>('');
  const [studyChapterFilter, setStudyChapterFilter] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isQuickActionMenuOpen, setIsQuickActionMenuOpen] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Settings and Quick Actions are now handled as regular sidebar panels
  // No special modal handling needed

  const handleToggleFullScreen = useCallback(() => {
    if (!isFullScreen) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  }, [isFullScreen]);

  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const handleDownload = useCallback(() => {
    // Download the book file
    const bookUrl = `/api/app/books-extn/download/${bookId}`;
    const link = document.createElement('a');
    link.href = bookUrl;
    link.download = `${metadata.title}.${format.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [bookId, metadata.title, format]);

  const handleStartFlashcardStudy = useCallback(
    (deckId: string, deckName?: string, chapterFilter: string | null = null) => {
      setStudyDeckId(deckId);
      setStudyDeckName(deckName || 'Flashcards');
      setStudyChapterFilter(chapterFilter);
      setActiveSidebarTab(null); // Close sidebar when starting study
    },
    [],
  );

  // Mobile bottom navigation items (5 primary tools)
  const navItems: NavItem[] = [
    {
      id: 'toc',
      label: t('App.Reader.Panel.Contents'),
      icon: <BookOpen className="w-5 h-5" />,
      active: activeSidebarTab === 'toc',
      onClick: () => setActiveSidebarTab(activeSidebarTab === 'toc' ? null : 'toc'),
      ariaLabel: t('App.Reader.Layout.NavContentsLabel'),
    },
    {
      id: 'search',
      label: t('App.Reader.Panel.Search'),
      icon: <Search className="w-5 h-5" />,
      active: activeSidebarTab === 'search',
      onClick: () => setActiveSidebarTab(activeSidebarTab === 'search' ? null : 'search'),
      ariaLabel: t('App.Reader.Layout.NavSearchLabel'),
    },
    {
      id: 'bookmarks',
      label: t('App.Reader.Panel.Bookmarks'),
      icon: <Bookmark className="w-5 h-5" />,
      active: activeSidebarTab === 'bookmarks',
      onClick: () => setActiveSidebarTab(activeSidebarTab === 'bookmarks' ? null : 'bookmarks'),
      badge: bookmarks?.length || undefined,
      ariaLabel: t('App.Reader.Layout.NavBookmarksLabel'),
    },
    {
      id: 'notes',
      label: t('App.Reader.Panel.Notes'),
      icon: <StickyNote className="w-5 h-5" />,
      active: activeSidebarTab === 'notes',
      onClick: () => setActiveSidebarTab(activeSidebarTab === 'notes' ? null : 'notes'),
      badge: notes?.length || undefined,
      ariaLabel: t('App.Reader.Layout.NavNotesLabel'),
    },
    {
      id: 'more',
      label: t('App.Reader.Layout.MoreOptionsTitle'),
      icon: <MoreVertical className="w-5 h-5" />,
      active: isMoreMenuOpen,
      onClick: () => setIsMoreMenuOpen(true),
      ariaLabel: t('App.Reader.Layout.NavMoreLabel'),
    },
  ];

  // More menu options (Flashcards, Print, Download, Settings, Info)
  const moreMenuOptions = [
    {
      id: 'flashcards',
      label: t('App.Reader.Layout.MoreFlashcards'),
      icon: <Layers className="w-5 h-5" />,
      onClick: () => {
        setActiveSidebarTab('flashcards');
        setIsMoreMenuOpen(false);
      },
    },
    {
      id: 'print',
      label: t('App.Reader.Layout.MorePrint'),
      icon: <Printer className="w-5 h-5" />,
      onClick: () => {
        setActiveSidebarTab('print');
        setIsMoreMenuOpen(false);
      },
    },
    {
      id: 'download',
      label: t('App.Reader.Layout.MoreDownload'),
      icon: <Download className="w-5 h-5" />,
      onClick: () => {
        handleDownload();
        setIsMoreMenuOpen(false);
      },
    },
    {
      id: 'settings',
      label: t('App.Reader.Layout.MoreSettings'),
      icon: <Settings className="w-5 h-5" />,
      onClick: () => {
        handleOpenSettings();
        setIsMoreMenuOpen(false);
      },
    },
    {
      id: 'info',
      label: t('App.Reader.Layout.MoreBookInfo'),
      icon: <Info className="w-5 h-5" />,
      onClick: () => {
        setActiveSidebarTab('info');
        setIsMoreMenuOpen(false);
      },
    },
  ];

  // Get panel title for bottom sheet
  const getPanelTitle = (tab: SidebarTab): string => {
    switch (tab) {
      case 'search': return t('App.Reader.Layout.PanelSearch');
      case 'toc': return t('App.Reader.Layout.PanelTOC');
      case 'bookmarks': return t('App.Reader.Layout.PanelBookmarks');
      case 'notes': return t('App.Reader.Layout.PanelNotes');
      case 'flashcards': return t('App.Reader.Layout.PanelFlashcards');
      case 'print': return t('App.Reader.Layout.PanelPrint');
      case 'download': return t('App.Reader.Layout.PanelDownload');
      case 'info': return t('App.Reader.Layout.PanelInfo');
      case 'layout': return t('App.Reader.Layout.PanelPageLayout');
      case 'settings': return t('App.Reader.Layout.PanelSettings');
      case 'quickAction': return t('App.Reader.Layout.PanelQuickActions');
      default: return '';
    }
  };

  return (
    <div className={styles.readerContainer} data-theme={theme}>
      {/* Header - Minimal (Logo, Settings, Download moved elsewhere) */}
      <ReaderHeader
        bookId={bookId}
        title={metadata.title}
        author={metadata.author}
        isFullScreen={isFullScreen}
        onToggleFullScreen={handleToggleFullScreen}
        currentChapterIndex={
          format === 'EPUB' && typeof navigation.currentLocation === 'number'
            ? navigation.currentLocation
            : undefined
        }
        currentPageNumber={
          format === 'PDF' && typeof navigation.currentLocation === 'number'
            ? navigation.currentLocation + 1  // Convert 0-based to 1-based for PDF
            : undefined
        }
      />

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Desktop: Floating Vertical Dock (macOS-style) */}
        {!isMobile && (
          <>
            <FloatingVerticalDock
              activeTab={activeSidebarTab}
              onTabChange={setActiveSidebarTab}
            />

            {/* Desktop: Floating Panel (slides in from right) */}
            {activeSidebarTab && (
              <FloatingPanel
                isOpen={!!activeSidebarTab}
                onClose={() => setActiveSidebarTab(null)}
                title={getPanelTitle(activeSidebarTab)}
                width="420px"
              >
                <ReaderSidebar
                  activeTab={activeSidebarTab}
                  onTabChange={setActiveSidebarTab}
                  tableOfContents={tableOfContents}
                  bookmarks={bookmarks}
                  notes={notes}
                  metadata={metadata}
                  format={format}
                  currentLocation={navigation.currentLocation}
                  currentLocationLabel={navigation.currentLocationLabel}
                  navigationCallbacks={navigationCallbacks}
                  actions={actions}
                  onStartFlashcardStudy={handleStartFlashcardStudy}
                  onAddPageNote={onAddPageNote}
                  hideTabsNav={true}
                  currentPage={navigation.currentPage}
                  totalPages={navigation.totalPages}
                  activeSecureContentChunkId={activeSecureContentChunkId}
                />
              </FloatingPanel>
            )}
          </>
        )}

        {/* Mobile: Bottom Navigation Bar */}
        {isMobile && (
          <MobileBottomNav items={navItems} />
        )}

        {/* Content Viewer (Format-Specific Renderer) */}
        <div className={styles.contentArea}>
          {children}
        </div>
      </div>

      {/* Bottom Toolbar */}
      <ReaderBottomToolbar
        navigation={navigation}
        navigationCallbacks={navigationCallbacks}
        showZoomControls={!!zoomControls}
        zoomLevel={zoomControls?.zoomLevel}
        onZoomIn={zoomControls?.onZoomIn}
        onZoomOut={zoomControls?.onZoomOut}
        onResetZoom={zoomControls?.onResetZoom}
      />

      {/* Settings Panel Modal */}
      <SettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      {/* Quick Action Menu */}
      <QuickActionMenu
        isOpen={isQuickActionMenuOpen}
        onClose={() => setIsQuickActionMenuOpen(false)}
        onAddNote={onAddPageNote}
        onAddBookmark={actions.onCreateBookmark}
        currentLocation={navigation.currentLocation}
        currentLocationLabel={navigation.currentLocationLabel}
      />

      {/* Flashcard Study Mode */}
      {studyDeckId && (
        <FlashcardStudyMode
          deckId={studyDeckId}
          deckName={studyDeckName}
          chapterFilter={studyChapterFilter}
          onClose={() => {
            setStudyDeckId(null);
            setStudyDeckName('');
            setStudyChapterFilter(null);
          }}
        />
      )}

      {/* Mobile: Floating Panel (slides up from bottom) */}
      {isMobile && activeSidebarTab && (
        <MobileFloatingPanel
          isOpen={!!activeSidebarTab}
          onClose={() => setActiveSidebarTab(null)}
          title={getPanelTitle(activeSidebarTab)}
        >
          <ReaderSidebar
            activeTab={activeSidebarTab}
            onTabChange={setActiveSidebarTab}
            tableOfContents={tableOfContents}
            bookmarks={bookmarks}
            notes={notes}
            metadata={metadata}
            format={format}
            currentLocation={navigation.currentLocation}
            currentLocationLabel={navigation.currentLocationLabel}
            navigationCallbacks={navigationCallbacks}
            actions={actions}
            onStartFlashcardStudy={handleStartFlashcardStudy}
            onAddPageNote={onAddPageNote}
            hideTabsNav={true}
            currentPage={navigation.currentPage}
            totalPages={navigation.totalPages}
            activeSecureContentChunkId={activeSecureContentChunkId}
          />
        </MobileFloatingPanel>
      )}

      {/* Mobile: More menu bottom sheet */}
      {isMobile && isMoreMenuOpen && (
        <BottomSheet
          isOpen={isMoreMenuOpen}
          onClose={() => setIsMoreMenuOpen(false)}
          title={t('App.Reader.Layout.MoreOptionsTitle')}
          height="auto"
          zIndex={1065}
        >
          <div className="flex flex-col gap-2 p-4">
            {moreMenuOptions.map((option) => (
              <button
                key={option.id}
                onClick={option.onClick}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-100 transition-colors text-left w-full"
              >
                {option.icon}
                <span className="text-base font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </BottomSheet>
      )}

      {/* Floating Actions (optional custom FABs) */}
      {floatingActions}
    </div>
  );
}

export default memo(UnifiedReaderLayout);
